/**
 * QdsSidebarMenu Web Component
 * 
 * A vertical sidebar navigation menu showing hierarchical items.
 * Suitable for permanent navigation in applications.
 * 
 * @element qds-sidebar-menu
 * 
 * @attr {String} data - JSON string containing menu items array
 * @attr {String} icon-root - Base URL for relative icon paths
 * @attr {Boolean} use-icons - Whether to display icons next to menu items (defaults to false)
 * @attr {Boolean} collapsed - Whether the sidebar is in collapsed state
 * @attr {Boolean} always-expanded - Whether all tree branches stay expanded and non-collapsible
 *
 * Item data may also carry `badge` (count/status chip), `disabled` (inert,
 * dimmed row) and `active` (pre-highlighted row, for hosts that derive the
 * active item from their router) — see HierarchicalMenuItem.
 * 
 * @csspart sidebar - The sidebar container
 * @csspart item - Menu items
 * @csspart toggle - Collapse/expand toggle button
 * 
 * @fires itemSelected - When a menu item is selected
 * @fires toggleCollapse - When the sidebar is collapsed/expanded
 */
import { QdsBaseMenu } from '../base-menu/qds-base-menu.js';
import { adoptPrimeIconsStyles, loadBaseCSS } from '../shared/button-utils.js';
import { hydrateQdsLucideIcons } from '../shared/icon-utils.js';
import { HierarchicalMenuItem } from '../../types/index.js';

loadBaseCSS();

interface ItemSelectedEventDetail {
  url: string;
  text: string;
}

interface ToggleCollapseEventDetail {
  collapsed: boolean;
}

class QdsSidebarMenu extends HTMLElement {
  // Private properties
  private _baseMenu: QdsBaseMenu;
  private _collapsed: boolean;
  private _alwaysExpanded: boolean;
  private _activeItem: string | null;
  private _boundItemClickHandler: ((event: MouseEvent) => void) | null;

  /**
   * Creates an instance of QdsSidebarMenu
   */
  constructor() {
    super();
    
    // Create the base menu instance
    this._baseMenu = new QdsBaseMenu();
    
    // Collapsed state
    this._collapsed = false;

    // Tree expansion mode
    this._alwaysExpanded = false;
    
    // Active item
    this._activeItem = null;
    
    // Event handler
    this._boundItemClickHandler = null;
    
    // Create shadow DOM
    this.attachShadow({ mode: "open" });
    
    // Initial render
    this.render();
  }

  static get observedAttributes(): string[] {
    return ["data", "icon-root", "use-icons", "collapsed", "always-expanded"];
  }

  /**
   * Called when an observed attribute changes
   * 
   * @param name - Name of the attribute that changed
   * @param oldValue - Previous value of the attribute
   * @param newValue - New value of the attribute
   */
  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    if (name === "data") {
      try {
        const data = JSON.parse(newValue);
        this._baseMenu.setData(data);
        this.render();
      } catch (e) {
        console.error("Invalid JSON data:", e);
      }
    }

    if (name === "icon-root") {
      this._baseMenu.setIconRoot(newValue);
      this.render();
    }

    if (name === "use-icons") {
      // Check if the attribute is explicitly set to 'false'
      this._baseMenu.useIcons = newValue !== null && newValue !== 'false';
      this.render();
    }
    
    if (name === "collapsed") {
      this._collapsed = newValue !== null;
      this.render();
    }

    if (name === "always-expanded") {
      this._alwaysExpanded = newValue !== null;
      this.render();
    }
  }

  /**
   * Called when the element is inserted into the DOM
   */
  connectedCallback(): void {
    if (!this.isConnected) return;

    // Add keyboard navigation support
    if (this.shadowRoot) {
      this.shadowRoot.addEventListener('keydown', this._handleKeydown.bind(this) as EventListener);
    }
  }
  
  /**
   * Called when the element is removed from the DOM
   */
  disconnectedCallback(): void {
    // Remove keyboard listener
    if (this.shadowRoot) {
      this.shadowRoot.removeEventListener('keydown', this._handleKeydown.bind(this) as EventListener);
    }
    
    // Remove click listener
    if (this._boundItemClickHandler && this.shadowRoot) {
      this.shadowRoot.removeEventListener('click', this._boundItemClickHandler as EventListener);
    }
  }
  
  /**
   * Handle keyboard navigation
   * @param event - Keyboard event
   */
  private _handleKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      const focusableElements = this.shadowRoot?.querySelectorAll('a.menu-item, button.menu-item');
      if (!focusableElements) return;
      
      const focusableArray = Array.from(focusableElements);
      const currentElement = document.activeElement;
      const currentIndex = focusableArray.indexOf(currentElement as Element);
      
      if (currentIndex >= 0) {
        let nextIndex;
        if (event.key === 'ArrowDown') {
          nextIndex = (currentIndex + 1) % focusableArray.length;
        } else {
          nextIndex = (currentIndex - 1 + focusableArray.length) % focusableArray.length;
        }
        (focusableArray[nextIndex] as HTMLElement).focus();
        event.preventDefault();
      }
    }
  }
  
  /**
   * Handle toggle click event
   */
  private _handleToggleClick(): void {
    this._collapsed = !this._collapsed;
    
    if (this._collapsed) {
      this.setAttribute('collapsed', '');
    } else {
      this.removeAttribute('collapsed');
    }
    
    // Dispatch custom event
    this.dispatchEvent(new CustomEvent<ToggleCollapseEventDetail>('toggleCollapse', { 
      detail: { collapsed: this._collapsed },
      bubbles: true,
      composed: true
    }));
  }
  
  /**
   * Handle item click event
   * @param event - Click event
   */
  private _handleItemClick(event: MouseEvent): void {
    // Check if we clicked on the collapse toggle button
    const target = event.target as HTMLElement;
    if (target.closest('.collapse-toggle')) {
      this._handleToggleClick();
      return;
    }
    
    const menuItem = target.closest('.menu-item') as HTMLElement | null;
    if (!menuItem) return;

    if (menuItem.classList.contains('category-static')) {
      return;
    }

    // Disabled rows are inert spans — no active highlight, no event.
    if (menuItem.classList.contains('disabled')) {
      return;
    }
    
    // Handle category toggle - check if we clicked the category button or its children (expander or text)
    if (!this._alwaysExpanded && (menuItem.classList.contains('category-button') || target.closest('.category-button'))) {
      // Get the parent category container
      const category = menuItem.closest('.sidebar-category') as HTMLElement | null;
      if (category) {
        // Toggle expanded state
        category.classList.toggle('expanded');
        
        // Update the expander icon
        const expander = category.querySelector('.expander') as HTMLElement | null;
        if (expander) {
          expander.textContent = category.classList.contains('expanded') ? '-' : '+';
        }
        
        // Update expanded state for screen readers
        const categoryButton = category.querySelector('.category') as HTMLElement | null;
        if (categoryButton) {
          const expandedState = category.classList.contains('expanded');
          categoryButton.setAttribute('aria-expanded', expandedState.toString());
        }
        return;
      }
    }
    
    // Set active item
    const activeItems = this.shadowRoot?.querySelectorAll('.menu-item.active');
    if (activeItems) {
      activeItems.forEach(item => {
        item.classList.remove('active');
        item.removeAttribute('aria-current');
      });
    }
    
    menuItem.classList.add('active');
    menuItem.setAttribute('aria-current', 'true');
    this._activeItem = this.getMenuItemText(menuItem);
    
    // Dispatch custom event for link items
    if (menuItem.hasAttribute('href')) {
      this.dispatchEvent(new CustomEvent<ItemSelectedEventDetail>('itemSelected', { 
        detail: { 
          url: menuItem.getAttribute('href') || '',
          text: this.getMenuItemText(menuItem)
        },
        bubbles: true,
        composed: true
      }));
    }
  }

  /**
   * Get whether icons are displayed
   * @returns True if icons are displayed, false otherwise
   */
  get useIcons(): boolean {
    return this._baseMenu.useIcons;
  }
  
  /**
   * Set whether icons should be displayed
   * @param value - True to display icons, false to hide them
   */
  set useIcons(value: boolean) {
    if (value) {
      this.setAttribute('use-icons', '');
    } else {
      this.setAttribute('use-icons', 'false');
    }
  }
  
  /**
   * Get whether the sidebar is collapsed
   * @returns True if collapsed, false otherwise
   */
  get collapsed(): boolean {
    return this._collapsed;
  }
  
  /**
   * Set whether the sidebar is collapsed
   * @param value - True to collapse, false to expand
   */
  set collapsed(value: boolean) {
    if (value) {
      this.setAttribute('collapsed', '');
    } else {
      this.removeAttribute('collapsed');
    }
  }

  /**
   * Get whether the tree is always expanded
   * @returns True if all branches stay expanded, false otherwise
   */
  get alwaysExpanded(): boolean {
    return this._alwaysExpanded;
  }

  /**
   * Set whether the tree should stay fully expanded
   * @param value - True to keep all branches expanded, false to allow toggling
   */
  set alwaysExpanded(value: boolean) {
    if (value) {
      this.setAttribute('always-expanded', '');
    } else {
      this.removeAttribute('always-expanded');
    }
  }

  /**
   * Create markup for category row content
   * @param item - Menu item to render
   * @param expanded - Whether the category is currently expanded
   * @returns HTML string for category content
   */
  private createCategoryContent(item: HierarchicalMenuItem, expanded: boolean): string {
    const iconHtml = this._baseMenu.useIcons ? this._baseMenu.generateIconHtml(item) : '';
    const expanderHtml = this._alwaysExpanded
      ? ''
      : `<span class="expander" aria-hidden="true">${expanded ? '-' : '+'}</span>`;

    return `
      <span class="menu-item-content">
        ${iconHtml}
        <span class="item-text">${item.name}</span>
      </span>
      ${expanderHtml}
    `;
  }

  /**
   * Get the visible text for a menu item
   * @param menuItem - Menu item element
   * @returns Item label text
   */
  private getMenuItemText(menuItem: HTMLElement): string {
    const textElement = menuItem.querySelector('.item-text') as HTMLElement | null;
    return textElement?.textContent?.trim() || menuItem.textContent?.trim() || '';
  }

  /**
   * Create per-level CSS variables for a menu row
   * @param level - Current tree level
   * @param kind - Menu row kind
   * @returns Inline style attribute value
   */
  private createLevelStyle(level: number, kind: 'category' | 'leaf'): string {
    const sectionColorFallback = 'var(--menu-muted-text-color, var(--theme-text-tertiary))';
    const categoryColorFallback = 'var(--menu-subtle-text-color, var(--theme-text-secondary))';
    const itemColorFallback = 'var(--menu-text-color, var(--theme-text-primary))';
    const categoryDefaults = {
      textTransform: level === 0 ? 'uppercase' : 'none',
      fontSize: level === 0
        ? 'var(--theme-font-size-xs, 12px)'
        : 'var(--theme-font-size-sm, 14px)',
      fontWeight: level === 0
        ? 'var(--theme-font-weight-semibold, 600)'
        : 'var(--theme-font-weight-medium, 500)',
      letterSpacing: level === 0 ? '0.12em' : 'normal',
      color: level === 0
        ? `var(--sidebar-section-color, ${sectionColorFallback})`
        : `var(--sidebar-category-color, ${categoryColorFallback})`,
      padding: level === 0
        ? 'var(--theme-space-3, 12px) var(--theme-space-4, 16px) var(--theme-space-2, 8px)'
        : 'var(--theme-space-2, 8px) var(--theme-space-4, 16px)',
      gap: 'var(--theme-space-2, 8px)'
    };
    const leafDefaults = {
      textTransform: 'none',
      fontSize: 'var(--theme-font-size-sm, 14px)',
      fontWeight: 'var(--theme-font-weight-medium, 500)',
      letterSpacing: 'normal',
      color: `var(--sidebar-item-color, ${itemColorFallback})`,
      padding: 'var(--theme-space-2, 8px) var(--theme-space-4, 16px)',
      gap: 'var(--theme-space-2, 8px)'
    };
    const defaults = kind === 'category' ? categoryDefaults : leafDefaults;

    return [
      `--sidebar-item-text-transform: var(--sidebar-level-${level}-text-transform, ${defaults.textTransform})`,
      `--sidebar-item-font-size: var(--sidebar-level-${level}-font-size, ${defaults.fontSize})`,
      `--sidebar-item-font-weight: var(--sidebar-level-${level}-font-weight, ${defaults.fontWeight})`,
      `--sidebar-item-letter-spacing: var(--sidebar-level-${level}-letter-spacing, ${defaults.letterSpacing})`,
      `--sidebar-item-color-override: var(--sidebar-level-${level}-color, ${defaults.color})`,
      `--sidebar-item-padding: var(--sidebar-level-${level}-padding, ${defaults.padding})`,
      `--sidebar-item-gap: var(--sidebar-level-${level}-gap, ${defaults.gap})`
    ].join('; ');
  }
  
  /**
   * Create sidebar menu HTML from the menu data
   * @returns HTML string for the sidebar menu
   */
  private createSidebarMenu(): string {
    const menuItems = this._baseMenu.menuData;
    if (!menuItems || menuItems.length === 0) {
      return '';
    }
    
    /**
     * Recursively generate sidebar menu HTML
     * @param items - Menu items to render
     * @param level - Current level in the hierarchy (0-based)
     * @returns HTML string for current level
     */
    const createMenuItems = (items: HierarchicalMenuItem[], level = 0): string => {
      return items.map(item => {
        // Check if item has children
        if (this._baseMenu.hasChildren(item)) {
          const isExpanded = this._alwaysExpanded || level === 0;
          const style = this.createLevelStyle(level, 'category');

          // This is a parent category with children
          return `
            <div class="sidebar-category level-${level} ${isExpanded ? 'expanded' : ''}">
                ${this._alwaysExpanded
                  ? `<div class="menu-item category category-static level-${level}" part="item" style="${style}">
                      ${this.createCategoryContent(item, true)}
                    </div>`
                  : `<button class="menu-item category category-button level-${level}" tabindex="0" type="button"
                        style="${style}"
                        aria-expanded="${isExpanded ? 'true' : 'false'}" part="item">
                      ${this.createCategoryContent(item, isExpanded)}
                    </button>`}
                <div class="category-items">
                    ${createMenuItems(item.items || [], level + 1)}
                </div>
            </div>
          `;
        } else {
          // This is a leaf item (no children)
          return `
            ${this._baseMenu.generateLinkHtml(item, {
              className: `menu-item level-${level}`,
              additionalAttributes: `tabindex="0" part="item" style="${this.createLevelStyle(level, 'leaf')}"`,
              includeIcon: this._baseMenu.useIcons,
              wrapContent: true
            })}
          `;
        }
      }).join('');
    };
    
    return `
      <div class="sidebar ${this._collapsed ? 'collapsed' : ''} ${this._alwaysExpanded ? 'always-expanded' : ''}" part="sidebar">
          <button class="collapse-toggle" aria-label="${this._collapsed ? 'Expand sidebar' : 'Collapse sidebar'}" part="toggle">
              ${this._collapsed
                ? '<svg class="chevron" viewBox="0 0 24 24" aria-hidden="true"><path d="m9 18 6-6-6-6"/></svg>'
                : '<svg class="chevron" viewBox="0 0 24 24" aria-hidden="true"><path d="m15 18-6-6 6-6"/></svg>'}
          </button>
          <!-- Optional rail heading (deal name, section label) — flows with
               the rail's own scroll, under the toggle's top line. -->
          <slot name="rail-header"></slot>
          <div class="sidebar-content">
              ${createMenuItems(menuItems)}
          </div>
      </div>
    `;
  }
  
  /**
   * Renders the component's HTML structure and attaches event listeners
   */
  private render(): void {
    if (!this.shadowRoot) return;
    
    this.shadowRoot.innerHTML = `
        <style>
            :host {
                --_sidebar-font-family: var(--qds-sidebar-font-family, var(--theme-font-family, var(--qds-font-family, 'IBM Plex Sans', sans-serif)));
                --_sidebar-font-size: var(--qds-sidebar-font-size, var(--theme-font-size-sm, 14px));
                --_sidebar-width: var(--sidebar-width, 250px);
                --_sidebar-collapsed-width: var(--sidebar-collapsed-width, 56px);
                --_sidebar-toggle-size: var(--qds-sidebar-toggle-size, calc(var(--theme-space-5, 20px) + var(--theme-space-1, 4px)));
                --_sidebar-toggle-offset: var(--qds-sidebar-toggle-offset, var(--theme-space-3, 12px));
                --_sidebar-toggle-font-size: var(--qds-sidebar-toggle-font-size, var(--theme-font-size-xs, 12px));
                --_sidebar-content-padding: var(--sidebar-content-padding, var(--theme-space-3, 12px) 0 var(--theme-space-4, 16px));
                --_sidebar-content-top-offset: var(--sidebar-content-top-offset, var(--theme-space-5, 20px));
                --_sidebar-category-spacing: var(--sidebar-category-spacing, var(--theme-space-1, 4px));
                --_sidebar-item-radius: var(--sidebar-item-border-radius, var(--theme-radius-sm, 4px));
                --_sidebar-item-icon-size: var(--sidebar-item-icon-size, calc(var(--theme-font-size-md, 16px) + var(--theme-space-1, 4px)));
                --_sidebar-nested-indent: var(--sidebar-nested-indent, var(--theme-space-4, 16px));
                --_sidebar-scrollbar-size: var(--qds-sidebar-scrollbar-size, var(--theme-space-2, 8px));
                --_sidebar-focus-ring: var(--button-focus-ring);
                --_sidebar-bg-color: var(--sidebar-bg-color, var(--theme-background-secondary));
                --_sidebar-text-color: var(--sidebar-text-color, var(--theme-text-primary));
                --_sidebar-border-color: var(--sidebar-border-color, var(--theme-border-primary));
                --_sidebar-toggle-bg-color: var(--sidebar-toggle-bg-color, var(--theme-background-primary));
                --_sidebar-hover-bg-color: var(--sidebar-item-hover-bg-color, var(--menu-button-hover-background, var(--button-hover-bg)));
                --_sidebar-active-bg-color: var(--sidebar-item-active-bg-color, var(--theme-background-accent-subtle));
                display: block;
                font-family: var(--_sidebar-font-family);
                font-size: var(--_sidebar-font-size);
                height: 100%;
                width: var(--_sidebar-width);
                min-width: var(--_sidebar-width);
                transition: width var(--transition-speed-normal, 0.25s) ease, min-width var(--transition-speed-normal, 0.25s) ease;
            }

            :host([collapsed]) {
                width: var(--_sidebar-collapsed-width);
                min-width: var(--_sidebar-collapsed-width);
            }
            
            *, *::before, *::after {
                box-sizing: border-box;
            }
            
            .sidebar {
                background-color: var(--_sidebar-bg-color);
                color: var(--_sidebar-text-color);
                width: var(--_sidebar-width);
                height: 100%;
                transition: width var(--transition-speed-normal, 0.25s) ease;
                position: relative;
                border-right: 1px solid var(--sidebar-edge-color, var(--_sidebar-border-color));
                overflow-y: auto;
                overflow-x: hidden;
                scrollbar-width: thin;
                scrollbar-color: var(--theme-border-strong) transparent;
            }
            
            .sidebar.collapsed {
                width: var(--_sidebar-collapsed-width);
            }
            
            .collapse-toggle {
                position: absolute;
                top: var(--_sidebar-toggle-offset);
                right: var(--_sidebar-toggle-offset);
                width: var(--_sidebar-toggle-size);
                height: var(--_sidebar-toggle-size);
                border-radius: var(--sidebar-toggle-radius, 8px);
                background-color: var(--_sidebar-toggle-bg-color);
                color: var(--sidebar-toggle-color, var(--_sidebar-text-color));
                border: 1px solid var(--_sidebar-border-color);
                cursor: pointer;
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 0;
                z-index: 10;
                transition: background-color var(--transition-speed-fast, 0.15s) ease, box-shadow var(--transition-speed-fast, 0.15s) ease;
            }

            .collapse-toggle .chevron {
                width: 16px;
                height: 16px;
                fill: none;
                stroke: currentColor;
                stroke-width: 2.5;
                stroke-linecap: round;
                stroke-linejoin: round;
            }

            .collapse-toggle:hover {
                background-color: var(--_sidebar-hover-bg-color);
            }

            .collapse-toggle:focus-visible {
                outline: 2px solid var(--theme-accent-primary);
                outline-offset: 2px;
                box-shadow: 0 0 0 4px var(--_sidebar-focus-ring);
            }
            
            .sidebar-content {
                padding: var(--_sidebar-content-padding);
                margin-top: var(--_sidebar-content-top-offset);
            }
            
            .menu-item {
                display: flex;
                align-items: center;
                justify-content: flex-start;
                gap: var(--sidebar-item-gap, var(--theme-space-2, 8px));
                padding: var(--sidebar-item-padding, var(--theme-space-2, 8px) var(--theme-space-4, 16px));
                text-decoration: none;
                color: var(--sidebar-item-color-override, inherit);
                cursor: pointer;
                transition: background-color var(--transition-speed-fast, 0.15s) ease, color var(--transition-speed-fast, 0.15s) ease;
                border: none;
                background: transparent;
                width: 100%;
                text-align: left;
                font-size: var(--sidebar-item-font-size, inherit);
                font-weight: var(--sidebar-item-font-weight, inherit);
                letter-spacing: var(--sidebar-item-letter-spacing, normal);
                text-transform: var(--sidebar-item-text-transform, none);
                border-radius: var(--_sidebar-item-radius);
                line-height: 1.4;
            }

            .category {
                justify-content: space-between;
            }

            .category-static {
                cursor: default;
            }

            .category-static:hover {
                background-color: transparent;
            }

            .menu-item-content {
                display: flex;
                align-items: center;
                gap: var(--sidebar-item-gap, var(--theme-space-2, 8px));
                min-width: 0;
                flex: 1;
            }
            
            .menu-item:hover {
                background-color: var(--_sidebar-hover-bg-color);
            }

            .menu-item:focus-visible {
                background-color: var(--_sidebar-hover-bg-color);
                outline: 2px solid var(--theme-accent-primary);
                outline-offset: -2px;
            }
            
            .menu-item.active {
                background-color: var(--_sidebar-active-bg-color);
                color: var(--sidebar-item-active-color, inherit);
                border-left: var(--sidebar-item-active-border-left, none);
                font-weight: var(--sidebar-item-active-font-weight, var(--theme-font-weight-semibold, 600));
            }

            .menu-item.disabled {
                opacity: 0.45;
                cursor: not-allowed;
                pointer-events: none;
            }

            .menu-item-badge {
                margin-left: auto;
                padding: 1px 7px;
                border-radius: 999px;
                font-size: var(--theme-font-size-xs, 12px);
                font-weight: var(--theme-font-weight-semibold, 600);
                line-height: 1.5;
                background-color: var(--sidebar-item-badge-bg-color, var(--theme-accent-primary));
                color: var(--sidebar-item-badge-color, var(--theme-text-on-accent-primary, #fff));
            }

            .sidebar.collapsed .menu-item-badge {
                display: none;
            }
            
            .menu-item .icon {
                width: var(--_sidebar-item-icon-size);
                height: var(--_sidebar-item-icon-size);
                min-width: var(--_sidebar-item-icon-size);
                flex-shrink: 0;
            }
            
            .sidebar-category {
                margin-bottom: var(--_sidebar-category-spacing);
            }
            
            .sidebar-category .category-items {
                padding-left: var(--_sidebar-nested-indent);
                max-height: 0;
                overflow: hidden;
                transition: max-height var(--transition-speed-normal, 0.25s) ease;
                display: none; /* Hide by default */
            }
            
            .sidebar-category.expanded > .category-items {
                max-height: 1000px; /* Arbitrary large height */
                overflow: visible;
                display: block; /* Show when expanded */
            }

            .sidebar.always-expanded .category-items {
                display: block;
                max-height: none;
                overflow: visible;
            }

            .sidebar.always-expanded .collapse-toggle {
                display: none;
            }

            .sidebar.always-expanded .sidebar-content {
                margin-top: 0;
            }
            
            .expander {
                display: inline-block;
                width: var(--theme-space-4, 16px);
                text-align: center;
                font-weight: var(--theme-font-weight-semibold, 600);
                flex-shrink: 0;
            }

            .category-static {
                cursor: default;
            }

            .category-static:hover,
            .category-static:focus {
                background-color: transparent;
            }

            .sidebar.always-expanded .level-0.category {
                cursor: default;
            }
            
            /* Collapsed state styles */
            .sidebar.collapsed .item-text {
                display: none;
            }
            
            .sidebar.collapsed .expander {
                opacity: 0;
                visibility: hidden;
            }
            
            .sidebar.collapsed .category-items {
                display: none;
            }
            
            @media (hover: hover) and (pointer: fine) {
                /* no-hover-expand opts out: a collapsed rail that balloons
                   over the page content on a stray cursor is not always the
                   wanted interaction (qec-aad's comp keeps it collapsed). */
                :host([collapsed]:not([no-hover-expand]):hover) {
                    width: var(--_sidebar-width);
                    min-width: var(--_sidebar-width);
                }

                /* Hover effect in collapsed state */
                :host(:not([no-hover-expand])) .sidebar.collapsed:hover {
                    width: var(--_sidebar-width);
                }

                :host(:not([no-hover-expand])) .sidebar.collapsed:hover .item-text {
                    display: inline-block;
                }

                :host(:not([no-hover-expand])) .sidebar.collapsed:hover .expander {
                    opacity: 1;
                    visibility: visible;
                }

                :host(:not([no-hover-expand])) .sidebar.collapsed:hover .sidebar-category.expanded > .category-items {
                    display: block;
                    max-height: 1000px;
                }
            }

            @media (hover: none), (pointer: coarse) {
                :host {
                    transition: none;
                }

                .sidebar {
                    transition: none;
                }
            }

            .sidebar::-webkit-scrollbar {
                width: var(--_sidebar-scrollbar-size);
            }

            .sidebar::-webkit-scrollbar-track {
                background: transparent;
            }

            .sidebar::-webkit-scrollbar-thumb {
                background-color: var(--theme-border-strong);
                border-radius: var(--theme-radius-sm, 4px);
                border: 2px solid transparent;
            }

            .sidebar::-webkit-scrollbar-thumb:hover {
                background-color: var(--theme-text-tertiary);
            }
        </style>
        ${this.createSidebarMenu()}
    `;
    
    // Add event listeners 
    // We handle all clicks through a single delegated event on the shadow root
    // This will catch clicks on the collapse toggle button as well as menu items
    if (this._boundItemClickHandler) {
      this.shadowRoot.removeEventListener('click', this._boundItemClickHandler as EventListener);
    }
    this._boundItemClickHandler = this._handleItemClick.bind(this);
    this.shadowRoot.addEventListener('click', this._boundItemClickHandler as EventListener);
    void adoptPrimeIconsStyles(this.shadowRoot);
    hydrateQdsLucideIcons(this.shadowRoot);
  }
}

// Register the component
if (!customElements.get("qds-sidebar-menu")) {
  customElements.define("qds-sidebar-menu", QdsSidebarMenu);
}

export { QdsSidebarMenu };
